import { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from "recharts";
import { TrendingUp as TrendUp, AlertTriangle as Warning, Share2 as ShareNetwork, Users as UsersThree } from "lucide-react";
import { Link } from "react-router-dom";

const BAND_COLORS = { low: "#16A34A", medium: "#EAB308", high: "#F97316", critical: "#DC2626" };

export default function Dashboard() {
  const { user } = useAuth();
  const [sum, setSum] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then(r => setSum(r.data)).catch(()=>{});
  }, []);

  if (!sum) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  const kpis = [
    { label: "Total Cases", value: sum.total_cases, icon: TrendUp },
    { label: "Critical Risk", value: sum.by_band.critical, icon: Warning, tone: "danger" },
    { label: "Pending Referrals", value: sum.referrals_pending, icon: ShareNetwork },
    { label: "Registered Users", value: sum.users_total, icon: UsersThree },
  ];

  const bandData = Object.entries(sum.by_band).map(([k,v]) => ({ name: k, value: v, color: BAND_COLORS[k] }));
  const catData = Object.entries(sum.by_category).map(([k,v]) => ({ name: k.replace("_"," "), value: v }));

  return (
    <div className="p-8 md:p-10 max-w-[1600px] mx-auto">
      <div className="mb-10">
        <div className="overline">Welcome, {ROLE_LABELS[user?.role]}</div>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">
          Operations Overview
        </h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          A single institutional view across cases, referrals and the field network.
        </p>
      </div>

      {/* KPI grid — Control Room */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-slate-200">
        {kpis.map((k,i) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g,"-")}`}
            className={`p-6 ${i < kpis.length-1 ? "lg:border-r border-slate-200" : ""} ${i%2===0 ? "border-r border-slate-200 lg:border-r" : ""} ${i < 2 ? "border-b lg:border-b-0 border-slate-200":""}`}>
            <div className="flex items-start justify-between">
              <div className="overline">{k.label}</div>
              <k.icon size={20} className={k.tone === "danger" ? "text-[#DC2626]" : "text-slate-400"} />
            </div>
            <div className={`font-heading font-extrabold mt-4 text-5xl tracking-tight ${k.tone === "danger" && k.value > 0 ? "text-[#DC2626]" : "text-slate-900"}`}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 border border-t-0 border-slate-200">
        <div className="lg:col-span-2 p-6 border-r border-slate-200">
          <div className="overline mb-4">Case Intake · Last 7 days</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sum.trend}>
                <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{fontSize:11}} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{fontSize:11}} stroke="#94a3b8" />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0033A0" strokeWidth={2} dot={{r:3}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-6">
          <div className="overline mb-4">Risk Distribution</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bandData}>
                <XAxis dataKey="name" tick={{fontSize:11}} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{fontSize:11}} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value">
                  {bandData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 border border-t-0 border-slate-200">
        <div className="lg:col-span-2 p-6 border-r border-slate-200">
          <div className="overline mb-4">Cases by Category</div>
          <div className="space-y-3">
            {catData.map(c => {
              const max = Math.max(...catData.map(d => d.value), 1);
              const pct = (c.value / max) * 100;
              return (
                <div key={c.name} className="grid grid-cols-[140px_1fr_40px] items-center gap-4 text-sm">
                  <div className="capitalize text-slate-600">{c.name}</div>
                  <div className="h-6 bg-slate-100 relative">
                    <div className="h-full bg-[#0033A0]" style={{width: `${pct}%`}} />
                  </div>
                  <div className="font-mono text-right">{c.value}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-6">
          <div className="overline mb-4">Quick Actions</div>
          <div className="space-y-3">
            <Link to="/cases" data-testid="quick-cases" className="block border border-slate-300 px-4 py-3 hover:bg-slate-900 hover:text-white transition-colors">
              <div className="text-sm font-semibold">Open Cases</div>
              <div className="text-xs text-slate-500 group-hover:text-slate-300">Review the full registry</div>
            </Link>
            <Link to="/referrals" data-testid="quick-referrals" className="block border border-slate-300 px-4 py-3 hover:bg-slate-900 hover:text-white transition-colors">
              <div className="text-sm font-semibold">Referrals</div>
              <div className="text-xs text-slate-500">Route to partner organizations</div>
            </Link>
            {["admin","executive","programme_manager"].includes(user?.role) && (
              <Link to="/users" data-testid="quick-users" className="block border border-slate-300 px-4 py-3 hover:bg-slate-900 hover:text-white transition-colors">
                <div className="text-sm font-semibold">Manage users</div>
                <div className="text-xs text-slate-500">Assign roles & permissions</div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
